<template>
  <div v-show="value" class="common-part-overlay" @click.self="close">
    <div class="common-part-panel" data-supply-drawer-keep @click.stop>
      <div class="drawer-head">
        <div>
          <div class="drawer-title">{{ '공용 품번 매핑' | translate }}</div>
          <div class="drawer-sub">{{ preview }}</div>
        </div>
        <button type="button" class="drawer-close" @click="close">×</button>
      </div>
      <div class="drawer-body">
        <dl class="info-grid">
          <div>
            <dt>{{ '제조사' | translate }}</dt>
            <dd>{{ context.manufacturerName || '-' }}</dd>
          </div>
          <div>
            <dt>{{ '프린터 모델' | translate }}</dt>
            <dd>{{ context.printerModelName || '-' }}</dd>
          </div>
          <div>
            <dt>{{ '구분' | translate }}</dt>
            <dd>{{ categoryLabel }} {{ colorLabel }}</dd>
          </div>
          <div>
            <dt>{{ '영향 슬롯' | translate }}</dt>
            <dd>{{ affectedSlotCount | currency }}</dd>
          </div>
          <div class="full">
            <dt>{{ '수집 설명' | translate }}</dt>
            <dd>
              <ul v-if="descriptions.length" class="desc-list">
                <li v-for="(d, i) in descriptions" :key="'d-' + i">{{ d }}</li>
              </ul>
              <span v-else>{{ context.description || '-' }}</span>
            </dd>
          </div>
        </dl>
        <p class="hint">
          {{
            '임대업체 자체 품번이 없을 때만 이 공용 품번을 씁니다. Admin에는 TN Market 카탈로그가 없어 품번을 직접 입력합니다.'
              | translate
          }}
        </p>
        <label class="field">
          <span>{{ '공용 품번' | translate }}</span>
          <input
            v-model="partNumber"
            type="text"
            class="map-input"
            :placeholder="'예: CF410A, NPG-67' | translate"
            @keydown.enter.prevent="save"
          />
        </label>
        <div class="actions">
          <button type="button" class="map-btn ghost" :disabled="!currentMap || saving" @click="unmap">
            {{ '매핑 해제' | translate }}
          </button>
          <button type="button" class="map-btn" :disabled="!canSave || saving" @click="save">
            {{ saving ? t('저장 중...') : t('저장') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PATH } from '@/constants'

export default {
  name: 'SupplyCommonPartDrawer',
  props: {
    value: { type: Boolean, default: false },
    context: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      saving: false,
      partNumber: '',
      currentMap: null,
      descriptions: [],
      affectedSlotCount: 0,
    }
  },
  computed: {
    categoryLabel() {
      const map = {
        TONER: this.t('토너'),
        DRUM: this.t('드럼'),
        WASTE: this.t('폐토너'),
        OTHER: this.t('기타'),
      }
      return map[this.context.supplyCategory] || this.context.supplyCategory || '-'
    },
    colorLabel() {
      const color = this.context.colorCode
      if (!color || String(color).toUpperCase() === 'UNKNOWN') return ''
      return color
    },
    canSave() {
      return !!String(this.partNumber || '').trim()
    },
    preview() {
      const model = [this.context.manufacturerName, this.context.printerModelName]
        .filter(Boolean)
        .join(' ')
      const desc = this.context.description || this.descriptions[0] || ''
      const part = this.partNumber || this.context.partNumber || '-'
      return `${model} · ${this.categoryLabel} ${desc} → ${part}`
    },
  },
  watch: {
    value(open) {
      if (open) this.loadWorkspace()
    },
    context: {
      deep: true,
      handler() {
        if (this.value) this.loadWorkspace()
      },
    },
  },
  methods: {
    t(text) {
      return this.$options.filters && this.$options.filters.translate
        ? this.$options.filters.translate(text)
        : text
    },
    close() {
      this.$emit('input', false)
    },
    requestBody() {
      return {
        printerModelId: this.context.printerModelId || 0,
        printerModelName: this.context.printerModelName || '',
        manufacturerName: this.context.manufacturerName || '',
        supplyCategory: this.context.supplyCategory || 'OTHER',
        colorCode: this.context.colorCode || 'UNKNOWN',
        description: this.context.description || '',
        partNumber: this.partNumber || this.context.partNumber || '',
      }
    },
    async loadWorkspace() {
      this.loading = true
      this.partNumber = this.context.partNumber || ''
      this.currentMap = null
      this.descriptions = []
      this.affectedSlotCount = Number(this.context.slotCount || 0)
      try {
        const res = await this.$request(PATH.MONITORING.SUPPLY_COMMON_PART_WORKSPACE)
          .setObject(this.requestBody())
          .catch()
          .enqueue()
        const data = res?.data || res || {}
        this.currentMap = data.supplyCommonPartId ? data : null
        this.partNumber = data.partNumber || this.context.partNumber || ''
        this.descriptions = Array.isArray(data.descriptions) ? data.descriptions : []
        this.affectedSlotCount = Number(data.affectedSlotCount || this.affectedSlotCount || 0)
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.canSave) return
      this.saving = true
      try {
        await this.$request(PATH.MONITORING.SUPPLY_COMMON_PART)
          .setObject(this.requestBody())
          .catch()
          .enqueue()
        this.$toasted?.show?.(this.t('공용 품번을 저장했습니다. 업체 매핑이 없는 슬롯에 적용됩니다.'))
        this.$emit('saved')
        this.close()
      } catch (e) {
        console.error(e)
        this.$toasted?.error?.(this.t('공용 품번 저장에 실패했습니다.'))
      } finally {
        this.saving = false
      }
    },
    async unmap() {
      this.saving = true
      try {
        await this.$request(PATH.MONITORING.SUPPLY_COMMON_PART_DELETE)
          .setObject(this.requestBody())
          .catch()
          .enqueue()
        this.$toasted?.show?.(this.t('공용 품번 매핑을 해제했습니다.'))
        this.$emit('saved')
        this.close()
      } catch (e) {
        console.error(e)
        this.$toasted?.error?.(this.t('매핑 해제에 실패했습니다.'))
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.common-part-overlay {
  position: fixed;
  inset: 0;
  z-index: 5200;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  justify-content: flex-end;
}
.common-part-panel {
  width: min(480px, 100%);
  height: 100%;
  background: #fff;
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.16);
  display: flex;
  flex-direction: column;
}
.drawer-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid #e2e8f0;
}
.drawer-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}
.drawer-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}
.drawer-close {
  border: 0;
  background: transparent;
  font-size: 22px;
  cursor: pointer;
  color: #64748b;
}
.drawer-body {
  padding: 16px 18px 24px;
  overflow: auto;
}
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;
  margin: 0 0 14px;
}
.info-grid .full {
  grid-column: 1 / -1;
}
.info-grid dt {
  font-size: 11px;
  color: #64748b;
  margin-bottom: 2px;
}
.info-grid dd {
  margin: 0;
  font-size: 13px;
  color: #0f172a;
}
.desc-list {
  margin: 0;
  padding-left: 16px;
}
.hint {
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  margin: 0 0 12px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
}
.map-input {
  height: 36px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 10px;
  font-size: 14px;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
}
.map-btn {
  height: 34px;
  border: 0;
  border-radius: 4px;
  background: #2563eb;
  color: #fff;
  padding: 0 14px;
  cursor: pointer;
  font-weight: 600;
}
.map-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.map-btn.ghost {
  background: #fff;
  color: #334155;
  border: 1px solid #cbd5e1;
}
</style>
